import { useTransition } from '@react-spring/web';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { FilteredGeneratorOutput } from '../../hooks/useGeneratorOutput';
import { ColorScale } from '../../utils/ColorScale';
import { DataKeyType, DefaultDatum } from '../../utils/DataProps';
import { getEmphasisLevel } from '../../utils/getEmphasisLevel';
import { NumberScale } from '../../utils/NumberScale';
import { TimeScale } from '../../utils/TimeScale';
import { DEEMPHASIZED_OPACITY } from '../../utils/transitions';
import { CreatePortalFn } from '../../utils/usePortal';
import { DefaultPointType } from '../../utils/XYDataProps';
import { getRangePoints } from '../Plot/Plot';
import { PointTransitionOutput } from './hooks/useCustomPointTransition';
import { GeneratedPointInfo } from './hooks/useInteractiveOverlay';
import { Point } from './Point';
import { XYPoint } from './XYPoint';

interface PointsProps<TDataKeyType extends DataKeyType, TPointType, TDatum> {
  /**
   * The data key for the active data item, for use when it may be controlled by an external
   * component. The component maintains its own internal active data item based on interactions that
   * are contained within the component.
   */
  activeSeriesDataKey: TDataKeyType | undefined;
  /**
   * Data about the active interactive point, if any.
   */
  activePointInfo: GeneratedPointInfo<TDataKeyType, TPointType, TDatum> | undefined;
  /**
   * The color scale to use.
   */
  colorScale: ColorScale<TDataKeyType>;
  /**
   * A function (analogous to ReactDOM.createPortal) that renders the given content in the
   * associated portal.
   */
  createPortal: CreatePortalFn;
  /**
   * Whether the data has changed.
   */
  dataChanged: boolean;
  /**
   * The filtered generator output.
   */
  filteredGeneratorOutput: FilteredGeneratorOutput<TDataKeyType, TPointType, TDatum>[];
  /**
   * Transition point values if needed
   */
  pointTransitionsByKey: PointTransitionOutput<TDataKeyType>;
  /**
   * Whether to enable animations.
   */
  isAnimated: boolean;
  /**
   * Whether the initial animation should be enabled when `isAnimated` is set to `true`.
   */
  isInitialAnimationEnabled: boolean;
  /**
   * The x-scale used for display.
   */
  xScale: TimeScale;
  /**
   * The y-scale used for display.
   */
  yScale: NumberScale;
}

interface PointsComponent {
  <TDataKeyType extends DataKeyType, TPointType = DefaultPointType, TDatum = DefaultDatum<TPointType[], TDataKeyType>>(
    props: PointsProps<TDataKeyType, TPointType, TDatum>,
  ): JSX.Element | null;
}

/**
 * Renders points representing the given data.
 */
export const Points = React.memo(
  <TDataKeyType extends DataKeyType, TPointType = DefaultPointType, TDatum = DefaultDatum<TPointType[], TDataKeyType>>({
    activeSeriesDataKey: activeSeriesDataKeyProp,
    activePointInfo,
    colorScale,
    createPortal,
    dataChanged,
    filteredGeneratorOutput,
    pointTransitionsByKey,
    isAnimated,
    isInitialAnimationEnabled,
    xScale,
    yScale,
  }: PointsProps<TDataKeyType, TPointType, TDatum>): JSX.Element | null => {
    type XYPointWithKey = XYPoint<TDataKeyType>;

    const [isInitialAnimation, setIsInitialAnimation] = useState(isAnimated && isInitialAnimationEnabled);
    const { max: plotBottom } = useMemo(() => getRangePoints(yScale), [yScale]);
    const animationAtRest = useRef<boolean>(true);

    const shouldStartAnimation = isAnimated && dataChanged;

    useEffect(() => {
      if (shouldStartAnimation) {
        // animationAtRest is set to false when we run this hook with isAnimated=true
        // and only set to true once animation is at rest with the onRest hook of useTransition.
        // Anytime data changes and isAnimated = true, the animation will run to completion without
        // being cancelled out by later renders.
        animationAtRest.current = false;
      }
    }, [shouldStartAnimation]);

    const xyPoints = useMemo(() => {
      return filteredGeneratorOutput.reduce<XYPointWithKey[]>((acc, series) => {
        return acc.concat(
          series.generatedPoints.map((point) => ({
            pointScaledValue: yScale(point[1] ?? 0),
            baselineValue: yScale(0),
            animationStartValue: yScale(point[0]),
            key: series.seriesDataKey,
            scaledXValue: xScale(point.data[0]),
            xValue: point.data[0],
          })),
        );
      }, []);
    }, [filteredGeneratorOutput, xScale, yScale]);

    const activeSeriesDataKey = activePointInfo?.seriesDataKey ?? activeSeriesDataKeyProp;

    const [transitionFn] = useTransition<
      XYPointWithKey,
      { cx: number | string; cy: number | string; opacity: number | string }
    >(
      xyPoints,
      {
        keys: (xyPoint: XYPointWithKey) => `${xyPoint.key}-${xyPoint.xValue.valueOf()}`,
        initial: (xyPoint) => ({ cx: xyPoint.scaledXValue, cy: xyPoint.pointScaledValue, opacity: 1 }),
        from: (xyPoint) => {
          const pointTransition = pointTransitionsByKey.get(xyPoint.key)?.get(xyPoint.xValue);
          if (pointTransition) {
            return {
              cx: pointTransition.from.cx,
              cy: pointTransition.from.cy,
              opacity: 1,
            };
          }
          return { cx: xyPoint.scaledXValue, cy: xyPoint.animationStartValue, opacity: 1 };
        },
        enter: (xyPoint) => ({ cx: xyPoint.scaledXValue, cy: xyPoint.pointScaledValue, opacity: 1 }),
        update: (xyPoint) => {
          const seriesEmphasisLevel = getEmphasisLevel(activeSeriesDataKey, xyPoint.key);

          const values = {
            cx: xyPoint.scaledXValue,
            cy: xyPoint.pointScaledValue,
            opacity: seriesEmphasisLevel === 'deemphasized' ? DEEMPHASIZED_OPACITY : 1,
          };
          return values;
        },
        leave: (xyPoint) => {
          const pointTransition = pointTransitionsByKey.get(xyPoint.key)?.get(xyPoint.xValue);
          if (pointTransition) {
            return {
              cx: pointTransition.to.cx,
              cy: pointTransition.to.cy,
              opacity: 0,
            };
          }
          return { cx: xyPoint.scaledXValue, cy: xyPoint.animationStartValue, opacity: 0 };
        },
        default: {
          immediate: !isAnimated || (!dataChanged && animationAtRest.current),
          onRest: () => {
            animationAtRest.current = true;
          },
        },
      },
      [activeSeriesDataKey, xyPoints, isAnimated, dataChanged, pointTransitionsByKey],
    );

    const handleOnAnimationEnd = useCallback<React.AnimationEventHandler>(() => {
      setIsInitialAnimation(false);
    }, []);

    return useMemo(() => {
      // we are setting the activePoint on every re-render of points because it should always be reset.
      let activePoint: React.ReactNode = null;

      const allPoints = transitionFn((props, item, transition, i) => {
        const { key: seriesDataKey, xValue } = item;

        const isActivePoint =
          seriesDataKey === activePointInfo?.seriesDataKey && xValue === activePointInfo?.generatedPoint.data[0];

        const isLastSeries = xyPoints.length - 1 === i;

        const point = (
          <Point
            key={transition.key as string}
            {...props}
            item={item}
            index={i}
            isActivePoint={isActivePoint}
            colorScale={colorScale}
            xScale={xScale}
            createPortal={createPortal}
            isInitialAnimation={isInitialAnimation}
            isLastSeries={isLastSeries}
            plotBottom={plotBottom}
            onInitialAnimationEnd={handleOnAnimationEnd}
          />
        );

        if (isActivePoint) {
          // we need to set the activePoint but not return it so that it will not be rendered as part of the element children initially
          activePoint = point;
          return null;
        }

        return point;
      });

      return <>{[...(allPoints.props as { children: React.ReactNode[] }).children, activePoint]}</>;
    }, [
      activePointInfo?.seriesDataKey,
      activePointInfo?.generatedPoint.data,
      xyPoints.length,
      colorScale,
      xScale,
      createPortal,
      isInitialAnimation,
      plotBottom,
      handleOnAnimationEnd,
      transitionFn,
    ]);
  },
) as PointsComponent;
